import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "@/lib/safeToast";
import { useAuth } from "@/lib/auth";

const DEFAULT_DESTINATION = "/laboratorio-pkpd";
const AUTHORIZED_EMAIL = "drcarlosaraujocastro@gmail.com";
const EMAIL_STORAGE_KEY = "pn-auth-email";

function initialEmail() {
  try {
    return window.localStorage.getItem(EMAIL_STORAGE_KEY) || AUTHORIZED_EMAIL;
  } catch {
    return AUTHORIZED_EMAIL;
  }
}

function friendlyAuthError(message?: string) {
  const normalized = String(message ?? "").toLowerCase();
  if (normalized.includes("invalid login credentials")) {
    return "E-mail ou senha incorretos. Confira a senha e tente novamente.";
  }
  if (normalized.includes("email not confirmed")) {
    return "Este e-mail ainda não foi confirmado.";
  }
  if (normalized.includes("rate limit") || normalized.includes("too many")) {
    return "Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.";
  }
  return message || "Não foi possível entrar.";
}

export default function AuthPage() {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const { session } = useAuth();

  const queryDestination = new URLSearchParams(location.search).get("from");
  const requestedDestination =
    typeof (location.state as { from?: string } | null)?.from === "string"
      ? (location.state as { from: string }).from
      : queryDestination?.startsWith("/") ? queryDestination : DEFAULT_DESTINATION;

  const isMySpaceDestination = requestedDestination.startsWith("/meu-espaco");
  const destinationLabel = isMySpaceDestination
    ? "Entrar em Meu Espaço"
    : "Entrar no Laboratório PK/PD";

  useEffect(() => {
    if (session) nav(requestedDestination, { replace: true });
  }, [session, nav, requestedDestination]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) return;

    try {
      window.localStorage.setItem(EMAIL_STORAGE_KEY, normalizedEmail);
    } catch {
      // O login continua funcionando mesmo quando o armazenamento local está indisponível.
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      if (error) throw error;
      nav(requestedDestination, { replace: true });
    } catch (error: any) {
      toast.error(friendlyAuthError(error?.message));
    } finally {
      setLoading(false);
    }
  }

  async function sendRecovery() {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      toast.error("Digite o e-mail da conta antes de recuperar a senha.");
      return;
    }

    setRecoveryLoading(true);
    try {
      const recoveryUrl = new URL("/auth/redefinir-senha", window.location.origin);
      recoveryUrl.searchParams.set("next", requestedDestination);

      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: recoveryUrl.toString(),
      });
      if (error) throw error;
      toast.success("E-mail de recuperação enviado. Abra o link para criar uma nova senha.");
    } catch (error: any) {
      toast.error(error?.message ?? "Não foi possível enviar a recuperação de senha.");
    } finally {
      setRecoveryLoading(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-background text-foreground px-4 py-[max(24px,env(safe-area-inset-top))] pb-[max(24px,env(safe-area-inset-bottom))] grid place-items-center">
      <Card className="w-full max-w-md overflow-hidden border-border/70 shadow-xl">
        <div className="bg-gradient-to-br from-primary/15 via-background to-background px-5 pt-6 pb-4 sm:px-8 sm:pt-8">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">PsicoNorte</div>
              <h1 className="text-2xl font-semibold leading-tight">Acesso clínico</h1>
            </div>
          </div>
          <p className="mt-4 text-sm leading-6 text-muted-foreground">
            Acesso privado. O cadastro público está desativado e somente a conta autorizada pode entrar.
          </p>
        </div>

        <form onSubmit={submit} className="px-5 pb-6 sm:px-8 sm:pb-8 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                name="username"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                className="h-12 pl-10 text-base"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="seuemail@exemplo.com"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="h-12 pl-10 pr-12 text-base"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={6}
                autoFocus
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="absolute right-1 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="h-12 w-full text-base">
            {loading ? "Entrando…" : destinationLabel}
          </Button>

          <button
            type="button"
            onClick={sendRecovery}
            disabled={recoveryLoading}
            className="w-full min-h-11 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
          >
            {recoveryLoading ? "Enviando recuperação…" : "Esqueci ou preciso redefinir minha senha"}
          </button>

          <div className="rounded-xl border bg-muted/35 p-3 text-xs leading-5 text-muted-foreground">
            O e-mail autorizado já vem preenchido. O iPhone pode salvar a senha nas Chaves do iCloud; o PsicoNorte nunca grava a senha no código ou no armazenamento local.
          </div>
        </form>
      </Card>
    </div>
  );
}
